import { Seo } from "@/components/Seo";
import { Img } from "@/components/Img";
import { CtaLink, PageHero, SectionHead } from "@/components/Ui";
import { Pillars, MarketGrowth } from "@/components/Blocks";
import { BrochureCta, TerritoryCta } from "@/components/CtaBands";
import { ADVANTAGE, LOCATIONS, MARKET, PATIENTS } from "@/content/site";

const GALLERY = [
  { name: "waiting-area", alt: "VidaVital Medical clinic waiting area with branded gold logo" },
  { name: "treatment-room", alt: "VidaVital Medical clinic examination and treatment room" },
  { name: "consultation", alt: "VidaVital Medical provider in a white coat consulting with a patient" },
  { name: "team-logo-wall", alt: "Two VidaVital Medical team members in front of the clinic logo wall" },
  { name: "exam-room", alt: "VidaVital Medical patient examination room" },
  { name: "lobby-booth", alt: "VidaVital Medical event display with banners in a building lobby" },
];

export default function Opportunity() {
  return (
    <>
      <Seo path="/opportunity" />
      <PageHero eyebrow={ADVANTAGE.eyebrow} title="The VidaVital Medical Franchise Opportunity">
        <p>{ADVANTAGE.p1}</p>
      </PageHero>

      <section className="section bg-ivory" aria-labelledby="advantage-heading">
        <div className="wrap grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionHead title={ADVANTAGE.title} id="advantage-heading"><p>{ADVANTAGE.p2}</p></SectionHead>
            <div className="mt-10"><Pillars items={ADVANTAGE.pillars} testId="opportunity-advantage-pillars" /></div>
            <div className="mt-10"><CtaLink to="/contact?intent=overview" id="opportunity-request-overview">Request the Franchise Overview</CtaLink></div>
          </div>
          <div className="lg:col-span-5">
            <Img name="staff-supplies" sizes="(min-width: 1024px) 38vw, 100vw" alt="VidaVital Medical staff member holding medical supplies inside the clinic" imgClassName="aspect-[4/5] w-full object-cover lg:sticky lg:top-28" />
          </div>
        </div>
      </section>

      <section className="section bg-white" aria-labelledby="footprint-heading">
        <div className="wrap">
          <SectionHead eyebrow="Multi-Market Momentum" title="Five Established Clinics Across Our Footprint" id="footprint-heading">
            <p>{ADVANTAGE.pillars[3].text}</p>
          </SectionHead>
          <ul className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-5" data-testid="locations-list">
            {LOCATIONS.map((l) => (
              <li key={l.name}>
                <Img name={l.img} sizes="(min-width: 768px) 20vw, 50vw" alt={l.alt} imgClassName={`aspect-[4/5] w-full object-cover ${l.pos || ""}`} />
                <p className="mt-3 font-display font-semibold">{l.name}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section on-dark bg-ink text-ivory" aria-labelledby="patients-heading">
        <div className="wrap grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Img name="staff-backdrop" sizes="(min-width: 1024px) 30vw, 100vw" alt="VidaVital Medical team member posing in front of the branded backdrop" imgClassName="aspect-[3/4] w-full object-cover" />
          </div>
          <div className="lg:col-span-8">
            <SectionHead eyebrow="Patient Demand" title={<span className="text-ivory">{PATIENTS.title}</span>} id="patients-heading">
              <p>{PATIENTS.p1}</p>
              <p>{PATIENTS.p2}</p>
            </SectionHead>
            <div className="mt-10"><Pillars items={PATIENTS.pillars} dark testId="patient-pillars" /></div>
          </div>
        </div>
      </section>

      <BrochureCta />

      <section className="section bg-ivory" aria-labelledby="market-heading">
        <div className="wrap grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionHead eyebrow={MARKET.eyebrow} title={MARKET.title} id="market-heading"><p>{MARKET.intro}</p></SectionHead>
            <ul className="mt-10 space-y-8" data-testid="market-trends">
              {MARKET.trends.map((t) => (
                <li key={t.title} className="border-l-2 border-gold pl-6">
                  <h3 className="text-xl font-semibold">{t.title}</h3>
                  <p className="mt-2 text-stone-600">{t.text}</p>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <Img name="clinician-coat" sizes="(min-width: 1024px) 38vw, 100vw" alt="VidaVital Medical clinician wearing a white medical coat" imgClassName="aspect-[2/3] max-h-[640px] w-full object-cover" />
          </div>
        </div>
        <div className="wrap mt-20"><MarketGrowth /></div>
      </section>

      <section className="section bg-white" aria-labelledby="gallery-heading">
        <div className="wrap">
          <SectionHead eyebrow="Inside VidaVital" title="Our Clinics and Team" id="gallery-heading" />
          <ul className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="clinic-gallery">
            {GALLERY.map((g) => (
              <li key={g.name}><Img name={g.name} sizes="(min-width: 768px) 33vw, 50vw" alt={g.alt} imgClassName="aspect-square w-full object-cover" /></li>
            ))}
          </ul>
        </div>
      </section>

      <TerritoryCta />
    </>
  );
}
